import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  RefreshCw,
  X,
  Filter,
  Ruler,
  Search
} from 'lucide-react';

/**
 * Dedicated filter for raw material (bar stock) inventory.
 * Two exact dimensions: diameter (直径) and length (长度/总长).
 * Filter values are only applied when the user clicks the search button.
 */
const RawMaterialFilter = ({
  filters,
  onAdvancedFilterChange,
  onFilterSubmit,
  onClearAllFilters,
  loading = false,
}) => {
  const appliedDiameter = filters?.tip_diameter || '';
  const appliedLength = filters?.total_length || '';

  // Local draft values; only committed to parent filters on search submit.
  const [draftDiameter, setDraftDiameter] = useState(appliedDiameter);
  const [draftLength, setDraftLength] = useState(appliedLength);

  // Sync local draft with parent filters when they change externally
  // (e.g. view switch, clear all filters).
  useEffect(() => {
    setDraftDiameter(appliedDiameter);
  }, [appliedDiameter]);

  useEffect(() => {
    setDraftLength(appliedLength);
  }, [appliedLength]);

  const activeCount = [appliedDiameter, appliedLength].filter(
    (v) => v !== '' && v != null
  ).length;

  const sanitizeNumber = (rawValue) => {
    // Allow typing decimals freely; strip any non-numeric / extra-dot input.
    return rawValue
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdvancedFilterChange('tip_diameter', draftDiameter);
    onAdvancedFilterChange('total_length', draftLength);
    onFilterSubmit();
  };

  const handleClear = () => {
    setDraftDiameter('');
    setDraftLength('');
    onAdvancedFilterChange('tip_diameter', '');
    onAdvancedFilterChange('total_length', '');
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Ruler className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">棒料库存筛选</h3>
            {activeCount > 0 && (
              <div className="relative ml-1">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Badge
                  variant="default"
                  className="absolute -top-2 -right-2 h-4 w-4 p-0 flex items-center justify-center text-xs"
                >
                  {activeCount}
                </Badge>
              </div>
            )}
          </div>
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onClearAllFilters}
              disabled={loading}
              title="清除所有筛选"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <form
          className="flex flex-wrap gap-4 items-end"
          onSubmit={handleSubmit}
        >
          <ExactField
            icon={<Ruler className="h-4 w-4 text-muted-foreground" />}
            label="直径 (mm)"
            value={draftDiameter}
            onChange={(value) => setDraftDiameter(sanitizeNumber(value))}
            placeholder="输入直径"
          />

          <ExactField
            icon={<Ruler className="h-4 w-4 text-muted-foreground" />}
            label="总长 (mm)"
            value={draftLength}
            onChange={(value) => setDraftLength(sanitizeNumber(value))}
            placeholder="输入总长"
          />

          <div className="flex items-center gap-2">
            <Button type="submit" disabled={loading}>
              <Search className="h-4 w-4 mr-2" />
              搜索
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleClear}
              disabled={loading || activeCount === 0}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              重置
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

const ExactField = ({ icon, label, value, onChange, placeholder }) => (
  <div className="flex-1 min-w-[220px] space-y-2">
    <Label className="flex items-center gap-2">
      {icon}
      {label}
    </Label>
    <NumberInput
      value={value}
      onChange={onChange}
      placeholder={placeholder}
    />
  </div>
);

const NumberInput = ({ value, onChange, placeholder }) => (
  <Input
    type="text"
    inputMode="decimal"
    pattern="^[0-9]*\.?[0-9]*$"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
  />
);

export default RawMaterialFilter;
